import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import SummaryCards from './SummaryCards';
import TrendChart from './TrendChart';
import MonthlyChart from './MonthlyChart';
import HoldingsTable from './HoldingsTable';
import { getSummary, getHoldings, getReturns, getTrend } from '../api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function Dashboard({ onLogout }) {
  const name       = localStorage.getItem('name') || '';
  const clientCode = localStorage.getItem('client_code') || '';
  const isMobile   = useIsMobile();

  const [summary,  setSummary]  = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [returns,  setReturns]  = useState(null);
  const [trend,    setTrend]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [pledgeOpen, setPledgeOpen] = useState(false);
  const [animating, setAnimating]   = useState(false);

  // Desktop genie refs
  const rightPanelRef = useRef(null);
  const [rightPanelMinimized, setRightPanelMinimized] = useState(false);
  const DURATION = 600;

  useEffect(() => {
    Promise.all([getSummary(), getHoldings(), getReturns(), getTrend('ALL')])
      .then(([s, h, r, t]) => {
        setSummary(s.data);
        setHoldings(h.data);
        setReturns(r.data);
        setTrend(t.data);
      })
      .finally(() => setLoading(false));
  }, []);

  // Desktop genie minimize
  function genieMinimize() {
    if (animating) return;
    setAnimating(true);
    const el = rightPanelRef.current;
    if (!el) return;
    const steps = 30;
    let frame = 0;
    const animate = () => {
      if (frame > steps) {
        el.style.opacity = '0';
        el.style.transform = 'scaleX(0.05) scaleY(0.05) translateY(100px)';
        el.style.clipPath = 'inset(90% 45% 0% 45% round 12px)';
        setRightPanelMinimized(true);
        setAnimating(false);
        return;
      }
      const t = frame / steps;
      const ease = t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
      el.style.transform = `translateY(${ease*120}px) scaleX(${1-ease*0.92}) scaleY(${1-ease*0.94}) skewX(${Math.sin(t*Math.PI)*6}deg)`;
      el.style.clipPath = `inset(${ease*88}% ${ease*40}% 0% ${ease*40}% round 12px)`;
      el.style.opacity = `${1 - ease*0.3}`;
      frame++;
      setTimeout(animate, DURATION / steps);
    };
    animate();
  }

  function genieRestore() {
    if (animating) return;
    setAnimating(true);
    setRightPanelMinimized(false);
    setTimeout(() => {
      const el = rightPanelRef.current;
      if (!el) return;
      el.style.transition = 'none';
      el.style.opacity = '0.2';
      el.style.transform = 'scaleX(0.05) scaleY(0.05) translateY(120px)';
      el.style.clipPath = 'inset(90% 45% 0% 45% round 12px)';
      const steps = 30;
      let frame = 0;
      const animate = () => {
        if (frame > steps) {
          el.style.transform = 'none';
          el.style.clipPath = 'none';
          el.style.opacity = '1';
          setAnimating(false);
          return;
        }
        const t = frame / steps;
        const ease = t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
        const inv = 1 - ease;
        el.style.transform = `translateY(${inv*120}px) scaleX(${0.08+inv*0.92}) scaleY(${0.06+inv*0.94}) skewX(${Math.sin(inv*Math.PI)*-6}deg)`;
        el.style.clipPath = `inset(${inv*88}% ${inv*40}% 0% ${inv*40}% round 12px)`;
        el.style.opacity = `${0.2 + ease*0.8}`;
        frame++;
        setTimeout(animate, DURATION / steps);
      };
      animate();
    }, 20);
  }

  // Mobile genie open/close
  function mobilePledgeOpen() {
    if (animating) return;
    setAnimating(true);
    setPledgeOpen(true);
    setTimeout(() => setAnimating(false), 500);
  }

  function mobilePledgeClose() {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setPledgeOpen(false);
      setAnimating(false);
    }, 400);
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column' }}>
      <Header name={name} clientCode={clientCode} onLogout={onLogout} />
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#888' }}>
        Loading your portfolio...
      </div>
    </div>
  );

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ minHeight:'100vh', background:'#f5f0ee', display:'flex', flexDirection:'column' }}>
        <Header name={name} clientCode={clientCode} onLogout={onLogout} />

        {/* Main scrollable content */}
        <div style={{ flex:1, overflowY:'auto', padding:'12px 12px 80px 12px' }}>
          {/* Breadcrumb */}
          <div style={{ fontSize:11, color:'#888', marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontWeight:600, color:'#333' }}>{name}</span>
            <span>·</span>
            <span>Code: {clientCode}</span>
          </div>

          <SummaryCards summary={summary} isMobile={true} />
          <TrendChart data={trend?.data || []} isMobile={true} />
          <MonthlyChart returns={returns?.returns || []} isMobile={true} />

          {/* Stock Pledge Table — inline on mobile */}
          <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
            <div style={{ padding:'12px 14px', borderBottom:'1px solid #f0e8e5', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#333' }}>Stock Pledge</div>
              <div style={{ fontSize:11, color:'#888' }}>{holdings?.holdings?.length || 0} scrips</div>
            </div>
            <HoldingsTable
              holdings={holdings?.holdings || []}
              totalMarket={holdings?.total_market_value}
              totalCollateral={holdings?.total_collateral_value}
              preview={false}
              isMobile={true}
            />
          </div>
        </div>

        {/* Fixed bottom button — mobile genie trigger */}
        <div style={{
          position:'fixed', bottom:0, left:0, right:0,
          background:'rgba(245,240,238,0.95)',
          backdropFilter:'blur(8px)',
          padding:'10px 16px',
          borderTop:'1px solid #e8e0dd',
          display:'flex', justifyContent:'center',
          zIndex:100,
        }}>
          <button
            onClick={mobilePledgeOpen}
            style={{
              padding:'10px 24px', borderRadius:20,
              background:'#6B1C2A', color:'#fff',
              border:'none', fontSize:13, fontWeight:600,
              cursor:'pointer', display:'flex', alignItems:'center', gap:6,
              boxShadow:'0 2px 12px rgba(107,28,42,0.3)',
            }}
          >
            <span>⌘</span> View Portfolio Summary
          </button>
        </div>

        {/* Mobile genie overlay — slides up from bottom */}
        {pledgeOpen && (
          <div style={{
            position:'fixed', inset:0, zIndex:200,
            background:'rgba(0,0,0,0.5)',
          }} onClick={mobilePledgeClose}>
            <div
              style={{
                position:'absolute', bottom:0, left:0, right:0,
                background:'#fff', borderRadius:'16px 16px 0 0',
                maxHeight:'85vh', overflowY:'auto',
                animation:'slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <style>{`
                @keyframes slideUp {
                  from { transform: translateY(100%); opacity: 0; }
                  to   { transform: translateY(0);    opacity: 1; }
                }
              `}</style>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #f0e8e5', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#333' }}>Portfolio Summary</div>
                <button onClick={mobilePledgeClose}
                  style={{ width:28, height:28, borderRadius:'50%', background:'#f5f0ee', border:'none', cursor:'pointer', fontSize:16, color:'#666' }}>
                  ×
                </button>
              </div>
              <div style={{ padding:'0 0 20px 0' }}>
                <SummaryCards summary={summary} isMobile={true} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── DESKTOP LAYOUT (unchanged) ─────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', background:'#f5f0ee' }}>
      <Header name={name} clientCode={clientCode} onLogout={onLogout} />

      <div style={{ display:'flex', flex:1, overflow:'hidden', height:'calc(100vh - 56px)', position:'relative' }}>
        {/* Left panel */}
        <div style={{ flex:1, overflowY:'auto', padding:20, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#888' }}>
              <span style={{ fontWeight:600, color:'#333' }}>{name}</span>
              <span>|</span>
              <span>Client Code : {clientCode}</span>
            </div>
            <div style={{ marginLeft:'auto' }}>
              {rightPanelMinimized ? (
                <button onClick={genieRestore} title="Restore Stock Pledge"
                  style={{ width:38, height:38, borderRadius:10, background:'#6B1C2A', border:'2px solid rgba(255,255,255,0.3)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 0 3px rgba(107,28,42,0.25)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                </button>
              ) : (
                <button onClick={genieMinimize} title="Hide Pledge Table"
                  style={{ padding:'4px 12px', borderRadius:8, fontSize:11, background:'rgba(107,28,42,0.08)', border:'1px solid #e0d0d2', color:'#6B1C2A', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ fontSize:14, lineHeight:1 }}>⌘</span> Hide Pledge Table
                </button>
              )}
            </div>
          </div>

          <SummaryCards summary={summary} />
          <TrendChart data={trend?.data || []} />
          <MonthlyChart returns={returns?.returns || []} />
          <HoldingsTable
            holdings={holdings?.holdings || []}
            totalMarket={holdings?.total_market_value}
            totalCollateral={holdings?.total_collateral_value}
            preview={true}
          />
        </div>

        {/* Right genie panel */}
        <div
          ref={rightPanelRef}
          style={{
            width:480, background:'#fff',
            borderLeft:'1px solid #e8e0dd',
            overflowY: rightPanelMinimized ? 'hidden' : 'auto',
            flexShrink:0, transformOrigin:'bottom right',
            willChange:'transform, clip-path, opacity',
            display: rightPanelMinimized ? 'none' : 'block',
          }}
        >
          <div style={{ padding:'14px 16px', borderBottom:'1px solid #f0e8e5', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#333', marginBottom:4 }}>Portfolio Summary</div>
              <div style={{ fontSize:11, color:'#888', marginBottom:10 }}>
                HOME / <span style={{ color:'#6B1C2A', fontWeight:500 }}>STOCK PLEDGE</span>
              </div>
            </div>
            <button onClick={genieMinimize}
              style={{ width:24, height:24, borderRadius:'50%', background:'#f5f0ee', border:'none', cursor:'pointer', fontSize:14, color:'#888', display:'flex', alignItems:'center', justifyContent:'center', marginLeft:10, flexShrink:0 }}>
              ×
            </button>
          </div>
          <HoldingsTable
            holdings={holdings?.holdings || []}
            totalMarket={holdings?.total_market_value}
            totalCollateral={holdings?.total_collateral_value}
            preview={false}
          />
        </div>
      </div>
    </div>
  );
}
